import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, HStack, VStack, Heading, Button, useColorMode } from '@chakra-ui/react';
import { JustifiedBars } from '../../icons';
import { useCalendarSettings, useIconColor, styles } from '../../../lib';

interface IDescriptionSectionProps {
	description: string;
	eventDetailLabel: string;
	showMoreLabel: string;
}

export const DescriptionSection = ({ description, eventDetailLabel, showMoreLabel }: IDescriptionSectionProps) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [shouldShowButton, setShouldShowButton] = useState(false);
	const { colorMode } = useColorMode();
	const { settings } = useCalendarSettings();
	const color = useIconColor();
	const descriptionRef = useRef<HTMLDivElement | null>(null);

	const handleToggle = useCallback(() => {
		setIsExpanded(!isExpanded);
	}, [isExpanded, setIsExpanded]);

	useEffect(() => {
		if (descriptionRef.current) {
			const height = descriptionRef.current.clientHeight;
			if (height > 100) {
				setShouldShowButton(true);
			} else {
				setIsExpanded(true);
			}
		}
	}, [description]);

	const htmlDescription = useMemo(() => ({ __html: description }), [description]);

	const { eventModalCardsColor } = settings.styles[colorMode];
	const { descriptionSx } = styles.eventDetailModal;
	return (
		<HStack m={2} p={6} bg={eventModalCardsColor} borderRadius='xl' alignItems='flex-start' justifyContent='center'>
			<Box>
				<JustifiedBars size={24} fill={color} />
			</Box>
			<VStack alignItems='flex-start' width='full'>
				<Heading as='h2' size='md' color={color}>
					{eventDetailLabel}
				</Heading>
				<Box position='relative' width='full' overflow='hidden'>
					<Box
						maxHeight={isExpanded ? 'none' : '200px'}
						overflow='hidden'
						transition='max-height 0.3s ease'
						position='relative'
					>
						<Box
							ref={descriptionRef}
							color={color}
							dangerouslySetInnerHTML={htmlDescription}
							sx={descriptionSx}
						/>
						<Box
							position='absolute'
							bottom='0'
							left='0'
							right='0'
							height='100px'
							bgGradient='linear(to-b, transparent 0%, gray.50 100%)'
							pointerEvents='none'
							transition='opacity 0.3s ease'
							opacity={isExpanded ? 0 : 1}
						/>
					</Box>
					{shouldShowButton && (
						<Button m={1} mt={2} onClick={handleToggle} colorScheme='blue'>
							{isExpanded ? 'Hide' : showMoreLabel}
						</Button>
					)}
				</Box>
			</VStack>
		</HStack>
	);
};
